import { _ as _define_property } from "@swc/helpers/_/_define_property";
import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import * as Sentry from '@sentry/react';
export const toWebBootData = (params)=>{
    const base = {
        app_id: window._intercom_app_id,
        // eslint-disable-next-line no-restricted-syntax -- pure transport formatter outside React; no getNow seam here. Intercom boot created_at.
        created_at: Date.now(),
        hide_default_launcher: params.hideDefaultLauncher
    };
    if (params.jwt != null) {
        return _object_spread_props(_object_spread({}, base), {
            intercom_user_jwt: params.jwt
        });
    }
    return _object_spread_props(_object_spread({}, base), {
        email: params.email,
        user_id: params.userId,
        user_hash: params.userHash
    });
};
export const makeIntercomClient = (web, native)=>({
        boot: (params)=>(native === null || native === void 0 ? void 0 : native.isAvailable) ? native.boot({
                userId: params.userId,
                email: params.email,
                jwt: params.jwt,
                userHash: params.userHash
            }) : web.boot(params),
        shutdown: ()=>(native === null || native === void 0 ? void 0 : native.isAvailable) ? void native.shutdown() : web.shutdown(),
        showNewMessage: ()=>(native === null || native === void 0 ? void 0 : native.isAvailable) ? void native.showNewMessage() : web.showNewMessage(),
        update: (data)=>web.update(data),
        hideIntercom: ()=>web.hideIntercom(),
        showIntercom: ()=>web.showIntercom(),
        onLoadFailed: (callback)=>web.onLoadFailed(callback),
        isLoaded: ()=>web.isLoaded()
    });
export class RealIntercomClient {
    boot(params) {
        return this.bootWithRetry(toWebBootData(params), 1, []);
    }
    bootWithRetry(data, attempt, previousFailures) {
        const MAX_RETRIES = 3;
        const RETRY_DELAY_MS = 1000;
        return this.getIntercom({
            skipReporting: true
        }).then((intercom)=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: "Intercom boot succeeded on attempt ".concat(attempt),
                level: 'info',
                data: {
                    attempt,
                    previousFailuresCount: previousFailures.length
                }
            });
            intercom('boot', data);
        }).catch((error)=>{
            // Extract diagnostics from the error (set by loadIntercom)
            const diagnostics = error.diagnostics;
            const failure = {
                attempt,
                diagnostics: diagnostics || {
                    likelyCause: 'unknown',
                    intercomFetchResult: 'unknown',
                    thirdPartyFetchResult: 'unknown',
                    cspViolations: [],
                    isOnline: navigator.onLine,
                    isPrivateBrowsing: null,
                    scriptLoadError: {
                        message: error instanceof Error ? error.message : String(error),
                        type: 'error',
                        raw: ''
                    }
                },
                // eslint-disable-next-line no-restricted-syntax -- diagnostic timestamp for Sentry boot-failure reporting; not test-observable
                timestamp: new Date().toISOString()
            };
            const allFailures = [
                ...previousFailures,
                failure
            ];
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: "Intercom boot failed on attempt ".concat(attempt, "/").concat(MAX_RETRIES),
                level: 'warning',
                data: {
                    attempt,
                    likelyCause: failure.diagnostics.likelyCause
                }
            });
            if (attempt < MAX_RETRIES) {
                return new Promise((resolve, reject)=>{
                    setTimeout(()=>{
                        this.bootWithRetry(data, attempt + 1, allFailures).then(resolve).catch(reject);
                    }, RETRY_DELAY_MS);
                });
            } else {
                this.reportLoadFailure(failure.diagnostics, {
                    attempts: allFailures,
                    bootData: data
                });
                throw new Error("Intercom boot failed after ".concat(MAX_RETRIES, " attempts"));
            }
        });
    }
    shutdown() {
        this.getIntercom().then((intercom)=>intercom('shutdown'));
    }
    showNewMessage() {
        this.getIntercom().then((intercom)=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: 'showNewMessage called successfully',
                level: 'info'
            });
            intercom('showNewMessage');
        }).catch(()=>{
            Sentry.addBreadcrumb({
                category: 'intercom',
                message: 'showNewMessage failed, redirecting to fallback',
                level: 'warning'
            });
            window.open('https://intercom.help/anchor-billing/en/', '_blank');
        });
    }
    update(data) {
        this.getIntercom().then((intercom)=>intercom('update', data));
    }
    hideIntercom() {
        this.getIntercom().then((intercom)=>intercom('update', {
                hide_default_launcher: true
            }));
    }
    showIntercom() {
        this.getIntercom().then((intercom)=>intercom('update', {
                hide_default_launcher: false
            }));
    }
    onLoadFailed(callback) {
        this.loadFailedCallbacks.push(callback);
    }
    isLoaded() {
        return this.loaded;
    }
    notifyLoadFailed(reason) {
        this.loadFailedCallbacks.forEach((cb)=>cb(reason));
    }
    getConsoleMessageForLikelyCause(diagnostics) {
        const baseMessage = '[Anchor Support Chat] Failed to load Intercom.';
        const { likelyCause, isPrivateBrowsing } = diagnostics;
        const privateBrowsingNote = isPrivateBrowsing ? ' Note: You appear to be using private/incognito browsing mode, which may have stricter privacy settings.' : '';
        switch(likelyCause){
            case 'offline':
                return "".concat(baseMessage, " You appear to be offline. Please check your internet connection and refresh the page.");
            case 'ad_blocker_or_privacy_extension':
                return "".concat(baseMessage, " This is likely caused by an ad blocker or privacy extension.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please add an exception for "intercom.io" in your ad blocker settings.');
            case 'intercom_specifically_blocked':
                return "".concat(baseMessage, " Intercom appears to be specifically blocked. This could be due to a browser extension, firewall, or corporate network policy.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please whitelist "intercom.io" or contact your IT administrator.');
            case 'script_execution_blocked':
                return "".concat(baseMessage, " The script was blocked from executing. This could be caused by a browser privacy setting, security extension, or firewall.").concat(privateBrowsingNote, ' To enable the Anchor Support chat, please check your browser\'s security settings or whitelist "intercom.io".');
            case 'csp_violation':
                return "".concat(baseMessage, " A Content Security Policy violation occurred. This may be caused by a browser extension modifying security headers.").concat(privateBrowsingNote, " To enable the Anchor Support chat, please disable extensions that modify CSP headers.");
            case 'network_timeout':
                return "".concat(baseMessage, " The request timed out. This could be due to slow network connectivity or a firewall blocking the connection.").concat(privateBrowsingNote, " Please check your network and try refreshing the page.");
            default:
                return "".concat(baseMessage, " The cause is unknown.").concat(privateBrowsingNote, " If you need support, please visit https://intercom.help/anchor-billing/en/");
        }
    }
    /**
     * Attempts to detect if the user is in private/incognito browsing mode.
     * Returns true if private browsing is detected, false if not, or null if detection failed.
     */ async detectPrivateBrowsing() {
        try {
            // Method 1: Check storage estimate (works in Chrome)
            if (navigator.storage && navigator.storage.estimate) {
                const { quota } = await navigator.storage.estimate();
                // In Chrome incognito, quota is typically limited to ~120MB
                if (quota && quota < 130 * 1024 * 1024) {
                    return true;
                }
            }
            // Method 2: Try to use IndexedDB (fails in Firefox private mode)
            return await new Promise((resolve)=>{
                const testDb = 'intercom-private-test';
                const request = indexedDB.open(testDb);
                request.onerror = ()=>{
                    // IndexedDB failed - likely Firefox private mode
                    resolve(true);
                };
                request.onsuccess = ()=>{
                    request.result.close();
                    indexedDB.deleteDatabase(testDb);
                    resolve(false);
                };
                // Timeout after 100ms
                setTimeout(()=>resolve(null), 100);
            });
        } catch (unused) {
            return null;
        }
    }
    async safeFetch(url) {
        let timeoutMs = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 3000;
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(()=>controller.abort(), timeoutMs);
            await fetch(url, {
                method: 'HEAD',
                mode: 'no-cors',
                signal: controller.signal
            });
            clearTimeout(timeoutId);
            return 'success';
        } catch (error) {
            if (error instanceof Error) {
                return error.name === 'AbortError' ? 'timeout' : "failed: ".concat(error.message);
            }
            return 'failed: unknown';
        }
    }
    async collectDiagnostics(scriptUrl, scriptLoadError) {
        const defaultDiagnostics = {
            likelyCause: 'unknown',
            intercomFetchResult: 'unknown',
            thirdPartyFetchResult: 'unknown',
            cspViolations: [],
            isOnline: navigator.onLine,
            isPrivateBrowsing: null,
            scriptLoadError
        };
        try {
            const [intercomFetchResult, thirdPartyFetchResult, isPrivateBrowsing] = await Promise.all([
                this.safeFetch(scriptUrl),
                this.safeFetch('https://www.google-analytics.com/analytics.js'),
                this.detectPrivateBrowsing()
            ]);
            const cspViolations = this.cspViolations;
            const intercomBlocked = intercomFetchResult.startsWith('failed');
            const thirdPartyBlocked = thirdPartyFetchResult.startsWith('failed');
            let likelyCause;
            if (!navigator.onLine) {
                likelyCause = 'offline';
            } else if (cspViolations.length > 0) {
                likelyCause = 'csp_violation';
            } else if (intercomBlocked && thirdPartyBlocked) {
                likelyCause = 'ad_blocker_or_privacy_extension';
            } else if (intercomBlocked && !thirdPartyBlocked) {
                likelyCause = 'intercom_specifically_blocked';
            } else if (intercomFetchResult === 'success') {
                likelyCause = 'script_execution_blocked';
            } else if (intercomFetchResult === 'timeout') {
                likelyCause = 'network_timeout';
            } else {
                likelyCause = 'unknown';
            }
            return {
                likelyCause,
                intercomFetchResult,
                thirdPartyFetchResult,
                cspViolations,
                isOnline: navigator.onLine,
                isPrivateBrowsing,
                scriptLoadError
            };
        } catch (diagnosticError) {
            Sentry.captureException(diagnosticError, {
                tags: {
                    context: 'intercom_diagnostics_collection_failed'
                }
            });
            return defaultDiagnostics;
        }
    }
    reportLoadFailure(diagnostics, bootContext) {
        try {
            this.notifyLoadFailed(diagnostics.likelyCause);
            // Log a user-friendly message to help CS identify the issue
            // eslint-disable-next-line no-console
            console.error(this.getConsoleMessageForLikelyCause(diagnostics));
            const isBootFailure = !!bootContext;
            Sentry.withScope((scope)=>{
                // Common tags for all failures
                scope.setTag('intercom_likely_cause', diagnostics.likelyCause);
                scope.setTag('intercom_fetch_result', diagnostics.intercomFetchResult);
                scope.setTag('intercom_third_party_blocked', diagnostics.thirdPartyFetchResult.startsWith('failed') ? 'true' : 'false');
                scope.setTag('intercom_has_csp_violations', diagnostics.cspViolations.length > 0 ? 'true' : 'false');
                let privateBrowsingValue = 'unknown';
                if (diagnostics.isPrivateBrowsing === true) {
                    privateBrowsingValue = 'true';
                } else if (diagnostics.isPrivateBrowsing === false) {
                    privateBrowsingValue = 'false';
                }
                scope.setTag('intercom_private_browsing', privateBrowsingValue);
                if (isBootFailure) {
                    // Boot failure: error level with all attempts data
                    scope.setTag('intercom_boot_attempts', String(bootContext.attempts.length));
                    scope.setContext('intercom_boot_failure', {
                        totalAttempts: bootContext.attempts.length,
                        bootDataKeys: Object.keys(bootContext.bootData),
                        attempts: bootContext.attempts.map((f)=>({
                                attempt: f.attempt,
                                timestamp: f.timestamp,
                                likelyCause: f.diagnostics.likelyCause,
                                intercomFetchResult: f.diagnostics.intercomFetchResult,
                                thirdPartyFetchResult: f.diagnostics.thirdPartyFetchResult,
                                isOnline: f.diagnostics.isOnline
                            }))
                    });
                    scope.setContext('intercom_final_diagnostics', diagnostics);
                    scope.setLevel('error');
                    Sentry.captureException(new Error("Intercom boot failed after ".concat(bootContext.attempts.length, " attempts: ").concat(diagnostics.likelyCause)));
                } else {
                    // Simple load failure: warning level
                    scope.setContext('intercom_diagnostics', diagnostics);
                    scope.setLevel('warning');
                    Sentry.captureException(new Error('failed to load intercom', {
                        cause: diagnostics.scriptLoadError
                    }));
                }
            });
        } catch (reportError) {
            Sentry.captureException(reportError, {
                tags: {
                    context: 'intercom_report_failure_failed'
                }
            });
        }
    }
    async getIntercom(options) {
        if (window.enableIntercom && window.isProduction) {
            if (this.failedToLoadIntercom) {
                this.failedToLoadIntercom = false;
                return this.loadIntercom(options);
            }
            if (this.loaded && window.Intercom) {
                return window.Intercom;
            }
            return this.loadIntercom(options);
        }
        return ()=>{};
    }
    loadIntercom(options) {
        var _ref;
        const APP_ID = window._intercom_app_id = 'yx1cchhq';
        const skipReporting = (_ref = options === null || options === void 0 ? void 0 : options.skipReporting) !== null && _ref !== void 0 ? _ref : false;
        Sentry.addBreadcrumb({
            category: 'intercom',
            message: 'Attempting to load Intercom script',
            level: 'info'
        });
        return new Promise((resolve, reject)=>{
            const w = window;
            const ic = w.Intercom;
            if (typeof ic === 'function' && this.loaded) {
                ic('reattach_activator');
                ic('update', w.intercomSettings);
                resolve(w.Intercom);
            } else {
                const d = document;
                const i = function i1() {
                    // eslint-disable-next-line prefer-rest-params
                    i.c(arguments);
                };
                i.q = [];
                i.c = function(args) {
                    i.q.push(args);
                };
                w.Intercom = i;
                const l = ()=>{
                    var _x_parentNode;
                    const s = d.createElement('script');
                    s.type = 'text/javascript';
                    s.async = true;
                    s.src = 'https://widget.intercom.io/widget/' + APP_ID;
                    const x = d.getElementsByTagName('script')[0];
                    (_x_parentNode = x.parentNode) === null || _x_parentNode === void 0 ? void 0 : _x_parentNode.insertBefore(s, x);
                    s.onload = ()=>{
                        this.loaded = true;
                        Sentry.addBreadcrumb({
                            category: 'intercom',
                            message: 'Intercom script loaded successfully',
                            level: 'info'
                        });
                        resolve(window.Intercom);
                    };
                    s.onerror = (error)=>{
                        this.failedToLoadIntercom = true;
                        // Extract serializable properties from the error event
                        const errorEvent = error;
                        const scriptLoadError = {
                            message: errorEvent.message || errorEvent.type || 'script load error',
                            type: errorEvent.type || 'error',
                            filename: errorEvent.filename,
                            lineno: errorEvent.lineno,
                            colno: errorEvent.colno,
                            raw: JSON.stringify(errorEvent)
                        };
                        this.collectDiagnostics(s.src, scriptLoadError).then((diagnostics)=>{
                            // Only report directly if not being handled by retry mechanism
                            if (!skipReporting) {
                                this.reportLoadFailure(diagnostics);
                            }
                            // Create error with diagnostics attached for retry mechanism
                            const err = new Error("Failed to load Intercom: ".concat(diagnostics.likelyCause));
                            err.diagnostics = diagnostics;
                            reject(err);
                        });
                    };
                };
                if (document.readyState === 'complete') {
                    l();
                } else if (w.attachEvent) {
                    w.attachEvent('onload', l);
                } else {
                    w.addEventListener('load', l, false);
                }
            }
        });
    }
    constructor(){
        _define_property(this, "loadFailedCallbacks", []);
        _define_property(this, "loaded", false);
        _define_property(this, "cspViolations", []);
        _define_property(this, "failedToLoadIntercom", false);
        document.addEventListener('securitypolicyviolation', (e)=>{
            var _e_blockedURI;
            if ((_e_blockedURI = e.blockedURI) === null || _e_blockedURI === void 0 ? void 0 : _e_blockedURI.includes('intercom')) {
                this.cspViolations.push("".concat(e.violatedDirective, ": ").concat(e.blockedURI));
            }
        });
    }
}
